import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Brain, Eye, EyeOff, UserPlus, Mail, Lock, User, HelpCircle, AlertTriangle, ChevronDown, ChevronUp } from 'lucide-react';
import { userService } from '../services/api';
import { getErrorMessage } from '../utils/helpers';
import HelpModal from '../components/HelpModal';
import SignupGuide from '../components/SignupGuide';

// ── 투자 책임 확인서 내용 ─────────────────────────────────────────────────────
const DISCLAIMER_TEXT = `본 AI 자동매매 시스템(이하 "시스템")을 이용함에 있어 아래 사항을 충분히 이해하고 동의합니다.

1. 투자 손실 책임
본 시스템이 제공하는 AI 분석 및 자동매매 기능은 투자 참고 자료이며, 투자로 인한 모든 손익(원금 손실 포함)의 책임은 전적으로 사용자 본인에게 있습니다. 시스템 운영자는 투자 손실에 대해 어떠한 법적·도의적 책임도 지지 않습니다.

2. 투자 원금 손실 가능성
주식 투자는 원금 손실이 발생할 수 있으며, 과거 수익률이 미래 수익을 보장하지 않습니다.

3. 시스템 오류 및 장애
API 연동 오류, 네트워크 장애, 증권사 시스템 점검 등으로 인한 미체결·오주문·지연 실행이 발생할 수 있으며, 이로 인한 손실에 대해 시스템 운영자는 책임을 지지 않습니다.

4. 자동매매 특성 이해
자동매매 특성상 설정된 조건에 따라 매매가 자동으로 실행됩니다. 사용자는 시스템을 정기적으로 모니터링할 의무가 있으며, 예상치 못한 매매가 발생하더라도 이를 이유로 이의를 제기하지 않습니다.

5. 개인정보 및 API 키 관리
사용자는 한국투자증권 API 키를 비롯한 모든 인증 정보를 안전하게 관리할 책임이 있습니다. 인증 정보 유출로 인한 피해는 사용자 본인이 책임집니다.

6. 서비스 변경 및 중단
시스템 운영자는 사전 통보 없이 서비스를 변경하거나 중단할 수 있습니다.

위 사항에 동의하며, 자기 책임 원칙 하에 본 시스템을 이용하겠습니다.`;

// ── 책임 확인서 컴포넌트 ──────────────────────────────────────────────────────
const DisclaimerSection = ({ agreed, onAgree }) => {
  const [expanded, setExpanded] = useState(false);

  return (
    <div className="rounded-xl border-2 border-amber-200 bg-amber-50 overflow-hidden">
      {/* 헤더 */}
      <div className="flex items-center gap-2 px-4 py-3 bg-amber-100">
        <AlertTriangle size={16} className="text-amber-600 flex-shrink-0" />
        <span className="text-sm font-bold text-amber-800 flex-1">
          투자 책임 확인서 (필수)
        </span>
        <button
          type="button"
          onClick={() => setExpanded(!expanded)}
          className="text-amber-600 hover:text-amber-800 transition-colors"
        >
          {expanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
        </button>
      </div>

      {/* 본문 펼치기 */}
      {expanded && (
        <div className="px-4 py-3 max-h-48 overflow-y-auto">
          <pre className="text-xs text-gray-700 whitespace-pre-wrap leading-relaxed font-sans">
            {DISCLAIMER_TEXT}
          </pre>
        </div>
      )}

      {/* 동의 체크박스 */}
      <div className="px-4 py-3 border-t border-amber-200">
        <label className="flex items-start gap-3 cursor-pointer">
          <input
            type="checkbox"
            checked={agreed}
            onChange={(e) => onAgree(e.target.checked)}
            className="mt-0.5 w-4 h-4 accent-amber-500 flex-shrink-0 cursor-pointer"
          />
          <span className="text-xs text-gray-700 leading-relaxed">
            위 투자 책임 확인서를 읽었으며, <strong>투자로 인한 모든 손익의 책임이 본인에게 있음</strong>을 이해하고 동의합니다.
          </span>
        </label>
        {!expanded && (
          <button
            type="button"
            onClick={() => setExpanded(true)}
            className="mt-1.5 ml-7 text-xs text-amber-600 underline hover:text-amber-800"
          >
            확인서 전문 보기
          </button>
        )}
      </div>
    </div>
  );
};


// ── 회원가입 페이지 ───────────────────────────────────────────────────────────
const RegisterPage = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirm_password: '',
    full_name: '',
  });
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showHelpModal, setShowHelpModal] = useState(false);
  const [disclaimerAgreed, setDisclaimerAgreed] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (formData.password !== formData.confirm_password) {
      setError('비밀번호가 일치하지 않습니다.');
      return;
    }

    if (!disclaimerAgreed) {
      setError('투자 책임 확인서에 동의해야 가입할 수 있습니다.');
      return;
    }

    setIsLoading(true);
    try {
      await userService.register(formData);
      // 동의한 약관 버전을 localStorage에 임시 보관 (첫 로그인 시 전송)
      localStorage.setItem('pending_disclaimer_version', '1.0.0');
      setSuccess('회원가입이 완료되었습니다. 이메일 인증 페이지로 이동합니다.');
      setTimeout(() => { navigate('/verify-email'); }, 2000);
    } catch (error) {
      setError(getErrorMessage(error));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        {/* 헤더 */}
        <div className="text-center">
          <div className="flex justify-center">
            <div className="w-16 h-16 bg-gradient-to-r from-blue-500 to-purple-600 rounded-2xl flex items-center justify-center shadow-lg">
              <Brain className="w-10 h-10 text-white" />
            </div>
          </div>
          <h2 className="mt-6 text-3xl font-bold text-gray-900">계정 생성</h2>
          <div className="flex items-center justify-center mt-2 space-x-2">
            <p className="text-sm text-gray-600">AI 트레이딩 시스템에 가입하세요</p>
            <button
              onClick={() => setShowHelpModal(true)}
              className="text-blue-600 hover:text-blue-800 transition-colors"
              title="가입 도움말"
            >
              <HelpCircle className="h-4 w-4" />
            </button>
          </div>
        </div>

        {/* 회원가입 폼 */}
        <div className="bg-white rounded-2xl shadow-xl p-8">
          <form className="space-y-6" onSubmit={handleSubmit}>
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-lg text-sm">
                {error}
              </div>
            )}
            {success && (
              <div className="bg-green-50 border border-green-200 text-green-600 px-4 py-3 rounded-lg text-sm">
                {success}
              </div>
            )}

            {/* 이름 */}
            <div>
              <label htmlFor="full_name" className="block text-sm font-medium text-gray-700 mb-2">이름</label>
              <div className="relative">
                <input
                  id="full_name" name="full_name" type="text" required
                  value={formData.full_name} onChange={handleChange}
                  className="w-full px-3 py-3 pl-10 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors"
                  placeholder="이름을 입력하세요"
                />
                <User className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
              </div>
            </div>

            {/* 이메일 */}
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">이메일</label>
              <div className="relative">
                <input
                  id="email" name="email" type="email" required
                  value={formData.email} onChange={handleChange}
                  className="w-full px-3 py-3 pl-10 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors"
                  placeholder="your@email.com"
                />
                <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
              </div>
            </div>

            {/* 비밀번호 */}
            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-2">비밀번호</label>
              <div className="relative">
                <input
                  id="password" name="password" type={showPassword ? 'text' : 'password'} required
                  value={formData.password} onChange={handleChange}
                  className="w-full px-3 py-3 pl-10 pr-10 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors"
                  placeholder="비밀번호 입력"
                />
                <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                <button type="button" onClick={() => setShowPassword(!showPassword)}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600">
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            {/* 비밀번호 확인 */}
            <div>
              <label htmlFor="confirm_password" className="block text-sm font-medium text-gray-700 mb-2">비밀번호 확인</label>
              <div className="relative">
                <input
                  id="confirm_password" name="confirm_password"
                  type={showConfirmPassword ? 'text' : 'password'} required
                  value={formData.confirm_password} onChange={handleChange}
                  className="w-full px-3 py-3 pl-10 pr-10 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors"
                  placeholder="비밀번호 다시 입력"
                />
                <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                <button type="button" onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600">
                  {showConfirmPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            {/* ── 투자 책임 확인서 ── */}
            <DisclaimerSection
              agreed={disclaimerAgreed}
              onAgree={setDisclaimerAgreed}
            />

            {/* 가입 버튼 */}
            <button
              type="submit"
              disabled={isLoading || !disclaimerAgreed}
              className="w-full flex justify-center items-center py-3 px-4 border border-transparent rounded-lg shadow-sm text-white bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 font-medium"
            >
              {isLoading ? (
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
              ) : (
                <UserPlus className="w-5 h-5 mr-2" />
              )}
              {isLoading ? '가입 중...' : '회원가입'}
            </button>
          </form>

          {/* 로그인 링크 */}
          <div className="mt-6 text-center">
            <p className="text-sm text-gray-600">
              이미 계정이 있으신가요?{' '}
              <Link to="/login" className="font-medium text-blue-600 hover:text-blue-500 transition-colors">
                로그인
              </Link>
            </p>
          </div>
        </div>
      </div>

      {/* 도움말 모달 */}
      <HelpModal isOpen={showHelpModal} onClose={() => setShowHelpModal(false)} title="회원가입 안내">
        <SignupGuide />
      </HelpModal>
    </div>
  );
};

export default RegisterPage;
